import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const API = process.env.REACT_APP_BACKEND_URL;

function TaskCard({ task, onUpdate, onDelete, onComment }) {
  const priorityColors = { high: 'bg-red-600', medium: 'bg-yellow-600', low: 'bg-green-600' };
  const statusColors = { pending: 'text-slate-400', in_progress: 'text-blue-400', completed: 'text-green-400' };
  const [showComments, setShowComments] = useState(false);
  const [comment, setComment] = useState('');

  return (
    <div data-testid={`task-${task.id}`} className="bg-slate-800/60 border border-slate-700 rounded-lg p-4 mb-2">
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${priorityColors[task.priority] || 'bg-slate-500'}`} />
            <h3 className={`font-medium text-sm ${task.status === 'completed' ? 'line-through text-slate-500' : 'text-white'}`}>{task.title}</h3>
            {task.tags?.includes('ai-generated') && <span className="text-[10px] bg-purple-900/40 text-purple-300 px-1.5 py-0.5 rounded">IA</span>}
          </div>
          {task.description && <p className="text-slate-400 text-xs mt-1 line-clamp-2">{task.description}</p>}
          <div className="flex gap-3 mt-2 text-xs text-slate-500">
            {task.assigned_to_name && <span>Asignado: {task.assigned_to_name}</span>}
            {task.due_date && <span>Vence: {task.due_date.substring(0, 10)}</span>}
            {task.estimated_hours > 0 && <span>{task.estimated_hours}h est.</span>}
            {task.subtasks?.length > 0 && <span>{task.subtasks.filter(s => s.completed).length}/{task.subtasks.length} subtareas</span>}
          </div>
        </div>
        <div className="flex gap-1 ml-2">
          <select value={task.status} onChange={e => onUpdate(task.id, { status: e.target.value })}
            className="bg-slate-900 text-xs border border-slate-600 rounded px-2 py-1 text-white">
            <option value="pending">Pendiente</option>
            <option value="in_progress">En Progreso</option>
            <option value="completed">Completada</option>
          </select>
          <Button size="sm" variant="ghost" className="text-xs text-slate-400" onClick={() => setShowComments(!showComments)}>
            {task.comments?.length || 0}
          </Button>
          <Button size="sm" variant="ghost" className="text-xs text-red-400" onClick={() => onDelete(task.id)}>X</Button>
        </div>
      </div>
      {showComments && (
        <div className="mt-3 border-t border-slate-700 pt-2">
          {(task.comments || []).map(c => (
            <div key={c.id} className="text-xs text-slate-400 mb-1"><strong>{c.created_by}:</strong> {c.text}</div>
          ))}
          <div className="flex gap-1 mt-1">
            <input className="flex-1 bg-slate-900 text-white border border-slate-600 rounded px-2 py-1 text-xs"
              placeholder="Comentar..." value={comment} onChange={e => setComment(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && comment) { onComment(task.id, comment); setComment(''); }}} />
          </div>
        </div>
      )}
    </div>
  );
}

export default function TaskManagerPage() {
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');
  const [filter, setFilter] = useState('all');
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', priority: 'medium', due_date: '', assigned_to_name: '' });

  const fetchData = useCallback(async () => {
    try {
      const [tasksRes, statsRes] = await Promise.all([
        fetch(`${API}/api/tasks`, { credentials: 'include' }),
        fetch(`${API}/api/tasks/stats`, { credentials: 'include' })
      ]);
      if (tasksRes.ok) setTasks(await tasksRes.json());
      if (statsRes.ok) setStats(await statsRes.json());
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  const showMsg = (t) => { setMsg(t); setTimeout(() => setMsg(''), 3000); };

  const handleCreate = async () => {
    const res = await fetch(`${API}/api/tasks`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(form)
    });
    if (res.ok) { showMsg('Tarea creada'); setShowAdd(false); setForm({ title: '', description: '', priority: 'medium', due_date: '', assigned_to_name: '' }); fetchData(); }
  };

  const handleUpdate = async (id, update) => {
    await fetch(`${API}/api/tasks/${id}`, {
      method: 'PUT', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(update)
    });
    fetchData();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminar tarea?')) return;
    await fetch(`${API}/api/tasks/${id}`, { method: 'DELETE', credentials: 'include' });
    fetchData();
  };

  const handleComment = async (id, text) => {
    await fetch(`${API}/api/tasks/${id}/comments`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text })
    });
    fetchData();
  };

  const filtered = filter === 'all' ? tasks : tasks.filter(t => t.status === filter);

  return (
    <div data-testid="task-manager-page" className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white">Gestor de Tareas</h1>
          <p className="text-slate-400 text-sm">{stats.total || 0} tareas | {stats.overdue || 0} vencidas</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="add-task-btn" onClick={() => setShowAdd(true)}>Nueva Tarea</Button>
        </div>
      </div>

      {msg && <div className="bg-blue-900/30 border border-blue-700 text-blue-200 px-4 py-2 rounded text-sm">{msg}</div>}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[['Total', stats.total, 'bg-slate-700'], ['Pendientes', stats.pending, 'bg-yellow-900/40'], ['En Progreso', stats.in_progress, 'bg-blue-900/40'], ['Completadas', stats.completed, 'bg-green-900/40'], ['Vencidas', stats.overdue, 'bg-red-900/40']].map(([label, val, bg]) => (
          <div key={label} className={`${bg} border border-slate-700 rounded-lg p-3`}>
            <p className="text-slate-400 text-xs">{label}</p>
            <p className="text-white text-xl font-bold">{val || 0}</p>
          </div>
        ))}
      </div>

      {showAdd && (
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader><CardTitle className="text-white">Nueva Tarea</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="md:col-span-2">
                <label className="text-slate-400 text-xs">Titulo</label>
                <input className="w-full bg-slate-900 text-white border border-slate-600 rounded px-3 py-2 text-sm mt-1" value={form.title} onChange={e => setForm({...form, title: e.target.value})} />
              </div>
              <div>
                <label className="text-slate-400 text-xs">Prioridad</label>
                <select className="w-full bg-slate-900 text-white border border-slate-600 rounded px-3 py-2 text-sm mt-1" value={form.priority} onChange={e => setForm({...form, priority: e.target.value})}>
                  <option value="high">Alta</option><option value="medium">Media</option><option value="low">Baja</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="text-slate-400 text-xs">Descripcion</label>
                <textarea className="w-full bg-slate-900 text-white border border-slate-600 rounded px-3 py-2 text-sm mt-1" rows={2} value={form.description} onChange={e => setForm({...form, description: e.target.value})} />
              </div>
              <div>
                <label className="text-slate-400 text-xs">Fecha limite</label>
                <input type="date" className="w-full bg-slate-900 text-white border border-slate-600 rounded px-3 py-2 text-sm mt-1" value={form.due_date} onChange={e => setForm({...form, due_date: e.target.value})} />
              </div>
            </div>
            <div className="flex gap-2 mt-3">
              <Button onClick={handleCreate} className="bg-green-600 hover:bg-green-700">Crear</Button>
              <Button variant="outline" onClick={() => setShowAdd(false)}>Cancelar</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList>
          <TabsTrigger value="all">Todas ({tasks.length})</TabsTrigger>
          <TabsTrigger value="pending">Pendientes</TabsTrigger>
          <TabsTrigger value="in_progress">En Progreso</TabsTrigger>
          <TabsTrigger value="completed">Completadas</TabsTrigger>
        </TabsList>
        <TabsContent value={filter} className="mt-4">
          {filtered.length === 0 ? (
            <p className="text-slate-500 text-center py-8">Sin tareas</p>
          ) : (
            filtered.map(t => <TaskCard key={t.id} task={t} onUpdate={handleUpdate} onDelete={handleDelete} onComment={handleComment} />)
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
